/**
 * Agents — Catalog of the seven specialist agents. Each card shows the agent's
 * purpose, the system prompt (truncated), tools it can call, guardrails, and
 * an "enabled" toggle. MOCKED.
 */
import { useState } from "react";
import MarketingShell from "@/components/MarketingShell";
import { Bot, PenSquare, Users, Compass, Share2, Mail, BarChart2, Shield, Activity, Edit3, Sparkles } from "lucide-react";

const AGENTS = [
  {
    id: "content", name: "Content Agent", icon: PenSquare, color: "#2E63B8", enabled: false,
    purpose: "Picks the right Design System template, fills named fields with on-brand copy, generates channel variants from one brief.",
    model: "claude-sonnet-4-5",
    tools: ["brand_library.lookup", "template.catalog", "asset.search", "content.search", "web_search", "image.generate"],
    guardrails: ["Max 6 variants/run", "PHI-firewalled CRM access", "Brand Compliance check pre-output"],
    runs7d: 0, success: 0,
  },
  {
    id: "audience", name: "Audience Agent", icon: Users, color: "#7A33FF", enabled: false,
    purpose: "Builds and refines segments from natural language. Reads de-identified CRM contacts only. Proposes look-alike expansion.",
    model: "claude-sonnet-4-5",
    tools: ["crm.contacts.read", "crm.leads.read", "audience.preview-count", "audience.lookalike"],
    guardrails: ["No PHI access", "Read-only on CRM data", "Max 10k contacts/segment"],
    runs7d: 0, success: 0,
  },
  {
    id: "seo-geo", name: "SEO / GEO Agent", icon: Compass, color: "#10A37F", enabled: false,
    purpose: "Tracks AI citation share, generates schema/JSON-LD, manages llms.txt, identifies content gaps vs competitors.",
    model: "claude-sonnet-4-5",
    tools: ["ai_engine.query", "schema.validate", "schema.generate", "llms_txt.read_write", "competitor.scan"],
    guardrails: ["Schema deploys to staging only — devops PR for prod", "Citation tracking budget $200/wk"],
    runs7d: 0, success: 0,
  },
  {
    id: "social", name: "Social Agent", icon: Share2, color: "#0077B5", enabled: false,
    purpose: "Picks template variant per channel, optimizes send time, queues via Ayrshare, reads back engagement.",
    model: "claude-sonnet-4-5",
    tools: ["ayrshare.post", "ayrshare.schedule", "engagement.read", "template.render"],
    guardrails: ["Brand Compliance pre-publish", "No auto-publish without human approval", "Rate-limit 50 posts/day"],
    runs7d: 0, success: 0,
  },
  {
    id: "email", name: "Email Agent", icon: Mail, color: "#D97757", enabled: false,
    purpose: "Subject/preview generation, send-time optimization, HTML rendering of Design System email templates, bounce processing.",
    model: "claude-sonnet-4-5",
    tools: ["resend.send", "resend.suppression", "deliverability.spam-score", "send-time.optimize"],
    guardrails: ["Brand Compliance pre-send", "Suppression list always honored", "One-click unsubscribe header required"],
    runs7d: 0, success: 0,
  },
  {
    id: "analyst", name: "Analyst Agent", icon: BarChart2, color: "#F59E0B", enabled: false,
    purpose: "Reads analytics tables, produces narratives not dashboards. Weekly digest, anomaly alerts, on-demand investigations.",
    model: "claude-sonnet-4-5",
    tools: ["analytics.query", "pipeline.query", "anomaly.detect", "report.render"],
    guardrails: ["No PII in narratives", "Read-only across all data sources"],
    runs7d: 0, success: 0,
  },
  {
    id: "brand", name: "Brand Compliance Agent", icon: Shield, color: "#EF4444", enabled: false,
    purpose: "Silent check on every draft. Flags off-voice phrasing, banned medical claims, unapproved product names, color/logo drift.",
    model: "claude-sonnet-4-5",
    tools: ["brand_library.read", "claims.banned-list", "voice.tone-check"],
    guardrails: ["Always run pre-output", "Auto-block on banned phrases (cure / diagnose / treat / HIPAA-certified)"],
    runs7d: 0, success: 0,
  },
];

export default function Agents() {
  const [agents, setAgents] = useState(AGENTS);
  const toggle = (id) => setAgents(agents.map((a) => a.id === id ? { ...a, enabled: !a.enabled } : a));

  return (
    <MarketingShell title="Agents" subtitle="Seven specialist agents · powered by Claude Sonnet 4.5">
      <div data-testid="agents-page">
        <div className="ai-banner" style={{ marginBottom: 22 }}>
          <div className="ai-pill"><Sparkles style={{ width: 11, height: 11 }} /> Agent registry</div>
          <p style={{ fontSize: 12.5 }}>
            Each agent has its own scope, tools, and guardrails. Outputs go through Brand Compliance before any human review.
            Disable an agent to take it offline globally. <strong>Auditable:</strong> every run is logged in Run History with full prompts, tool calls, and artifacts.
          </p>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 14 }}>
          {agents.map((a) => {
            const Icon = a.icon;
            return (
              <div key={a.id} className="mkt-card" style={{ padding: "18px 20px" }} data-testid={`agent-${a.id}`}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 12 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 10, background: `${a.color}15`, color: a.color, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <Icon style={{ width: 16, height: 16 }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <h3 style={{ fontFamily: "var(--font-head)", fontSize: 14, fontWeight: 620, color: "var(--navy)" }}>{a.name}</h3>
                      <span className={`tag ${a.enabled ? "tag-green" : "tag-gray"}`}>{a.enabled ? "Enabled" : "Off"}</span>
                    </div>
                    <p style={{ fontSize: 12, color: "var(--text-secondary)", marginTop: 4, lineHeight: 1.5 }}>{a.purpose}</p>
                  </div>
                  <label style={{ display: "inline-flex", alignItems: "center", cursor: "pointer", flexShrink: 0 }}>
                    <input type="checkbox" checked={a.enabled} onChange={() => toggle(a.id)} style={{ accentColor: "var(--blue)", width: 16, height: 16 }} data-testid={`agent-toggle-${a.id}`} />
                  </label>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, fontSize: 11, marginBottom: 12 }}>
                  <div>
                    <div className="eyebrow" style={{ marginBottom: 3 }}>Model</div>
                    <div style={{ fontFamily: "var(--font-mono)", color: "var(--text-secondary)" }}>{a.model}</div>
                  </div>
                  <div>
                    <div className="eyebrow" style={{ marginBottom: 3 }}>Runs · 7d</div>
                    <div className="num" style={{ color: "var(--text-secondary)", fontSize: 13 }}>
                      {a.runs7d} <span style={{ fontSize: 10, color: "var(--text-tertiary)" }}>({a.success}% ok)</span>
                    </div>
                  </div>
                </div>

                <div style={{ marginBottom: 10 }}>
                  <div className="eyebrow" style={{ marginBottom: 4 }}>Tools</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                    {a.tools.map((t) => <span key={t} className="tag tag-blue" style={{ fontFamily: "var(--font-mono)", fontSize: 10 }}>{t}</span>)}
                  </div>
                </div>

                <div style={{ marginBottom: 12 }}>
                  <div className="eyebrow" style={{ marginBottom: 4 }}>Guardrails</div>
                  <ul style={{ margin: 0, paddingLeft: 14, fontSize: 11, color: "var(--text-tertiary)", lineHeight: 1.6 }}>
                    {a.guardrails.map((g, i) => <li key={i}>{g}</li>)}
                  </ul>
                </div>

                <div style={{ display: "flex", gap: 6 }}>
                  <button className="btn" style={{ height: 28, fontSize: 11 }}><Edit3 style={{ width: 11, height: 11 }} /> System prompt</button>
                  <button className="btn" style={{ height: 28, fontSize: 11 }}><Activity style={{ width: 11, height: 11 }} /> Recent runs</button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </MarketingShell>
  );
}
